// canon workflow store (scaffold stub). Persist only the session ID;
// turns reload from the authenticated API and streaming state is always transient. The node-builder refines this once
// the workflow's events + result shape firm up. See decomposing-workflows/reference/store.md.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import { ApiError, fetchCanonSession, streamCanon, type WorkflowEvent } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'

const STORE_KEY = 'canon-store-v2'
const LEGACY_STORE_KEY = 'canon-store-v1'
const USER_ID_KEY = 'canon-store-user-id'

try {
  localStorage.removeItem(LEGACY_STORE_KEY)
} catch {
  // localStorage can be unavailable in non-browser rendering.
}

export type Role = 'user' | 'assistant'
export interface Message {
  id: string
  role: Role
  content: string
  createdAt: number
}

interface CanonState {
  sessionId: string | null
  messages: Message[]
  streaming: boolean
  hydrated: boolean
  error: string | null
  sendMessage: (content: string) => Promise<void>
  loadSession: () => Promise<void>
  reset: () => void
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

let streamGeneration = 0
let reloadGeneration = 0

export const useCanonStore = create<CanonState>()(
  persist(
    (set, get) => ({
      sessionId: null,
      messages: [],
      streaming: false,
      hydrated: false,
      error: null,

      sendMessage: async (content) => {
        const { sessionId, streaming, hydrated } = get()
        if (streaming || !hydrated || !content.trim()) return
        const initiatingUserId = useAuthStore.getState().user?.id
        if (!initiatingUserId) return
        const generation = ++streamGeneration
        const isCurrentStream = () =>
          generation === streamGeneration &&
          useAuthStore.getState().user?.id === initiatingUserId
        const asstId = newId()
        set((s) => ({
          messages: [
            ...s.messages,
            { id: newId(), role: 'user', content, createdAt: Date.now() },
            { id: asstId, role: 'assistant', content: '', createdAt: Date.now() },
          ],
          streaming: true,
          error: null,
        }))
        let acc = ''
        try {
          await streamCanon(
            { content, session_id: sessionId, run_mode: 'fast' },
            {
            onText: (delta) => {
              if (!isCurrentStream()) return
              acc += delta
              set((s) => ({ messages: s.messages.map((m) => (m.id === asstId ? { ...m, content: acc } : m)) }))
            },
            onDone: (ev: WorkflowEvent) => {
              if (!isCurrentStream()) return
              if (typeof ev.session_id === 'string') {
                const currentUserId = useAuthStore.getState().user?.id
                try {
                  if (currentUserId) localStorage.setItem(USER_ID_KEY, currentUserId)
                } catch {
                  // The backend still enforces ownership when storage is unavailable.
                }
                set({ sessionId: ev.session_id })
              }
              if (!acc && typeof ev.response === 'string') {
                const resp = String(ev.response)
                set((s) => ({ messages: s.messages.map((m) => (m.id === asstId ? { ...m, content: resp } : m)) }))
              }
              set({ streaming: false })
            },
              onError: (msg) => {
                if (isCurrentStream()) set({ error: msg, streaming: false })
              },
            }
          )
        } finally {
          // Transport completion is authoritative even if a proxy or future
          // workflow omits the session_complete domain event.
          if (isCurrentStream()) set({ streaming: false })
        }
      },

      loadSession: async () => {
        const { sessionId } = get()
        const currentUserId = useAuthStore.getState().user?.id ?? null
        const generation = ++reloadGeneration
        const isCurrentReload = () =>
          generation === reloadGeneration &&
          useAuthStore.getState().user?.id === currentUserId &&
          get().sessionId === sessionId
        let storedUserId: string | null = null
        try {
          storedUserId = localStorage.getItem(USER_ID_KEY)
        } catch {
          // The backend ownership check remains authoritative.
        }
        if (!currentUserId || (storedUserId && storedUserId !== currentUserId)) {
          try {
            localStorage.removeItem(STORE_KEY)
            if (currentUserId) localStorage.setItem(USER_ID_KEY, currentUserId)
            else localStorage.removeItem(USER_ID_KEY)
          } catch {
            // In-memory state is still cleared below.
          }
          set({ sessionId: null, messages: [], hydrated: true, error: null })
          return
        }
        try {
          localStorage.setItem(USER_ID_KEY, currentUserId)
        } catch {
          // The backend ownership check remains authoritative.
        }
        if (!sessionId) {
          set({ hydrated: true, error: null })
          return
        }
        set({ hydrated: false, error: null })
        try {
          const turns = await fetchCanonSession(sessionId)
          if (!isCurrentReload() || get().sessionId !== sessionId || get().streaming) return
          set({
            messages: turns.map((turn, index) => ({
              id: `${sessionId}-${index}`,
              role: turn.role,
              content: turn.content,
              createdAt: index,
            })),
            error: null,
          })
        } catch (error) {
          if (!isCurrentReload()) return
          if (error instanceof ApiError && error.status === 404) {
            try {
              localStorage.removeItem(STORE_KEY)
            } catch {
              // In-memory state is still cleared below.
            }
            set({ sessionId: null, messages: [], hydrated: true, error: null })
          } else {
            set({ error: error instanceof Error ? error.message : String(error) })
          }
        } finally {
          if (isCurrentReload()) set({ hydrated: true })
        }
      },

      reset: () => {
        streamGeneration += 1
        reloadGeneration += 1
        set({ sessionId: null, messages: [], streaming: false, hydrated: true, error: null })
      },
    }),
    {
      name: STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist only the durable identifier; content reloads from the API.
      partialize: (state) => ({ sessionId: state.sessionId }),
    }
  )
)

let observedAuthUserId = useAuthStore.getState().user?.id ?? null
useAuthStore.subscribe((state) => {
  const nextUserId = state.user?.id ?? null
  if (nextUserId === observedAuthUserId) return
  streamGeneration += 1
  reloadGeneration += 1
  let storedUserId: string | null = null
  try {
    storedUserId = localStorage.getItem(USER_ID_KEY)
  } catch {
    // Continue with the conservative reset below.
  }
  const isMatchingInitialHydration =
    observedAuthUserId === null && nextUserId !== null && storedUserId === nextUserId
  observedAuthUserId = nextUserId
  if (isMatchingInitialHydration) {
    useCanonStore.setState({ hydrated: false })
    return
  }
  try {
    localStorage.removeItem(STORE_KEY)
    if (nextUserId) localStorage.setItem(USER_ID_KEY, nextUserId)
    else localStorage.removeItem(USER_ID_KEY)
  } catch {
    // The in-memory reset below still prevents cross-user disclosure.
  }
  // Auth listeners run synchronously with the identity change, before the
  // next render can expose the previous user's in-memory messages.
  useCanonStore.setState({
    sessionId: null,
    messages: [],
    streaming: false,
    hydrated: nextUserId !== null,
    error: null,
  })
})
